export type ApiFetchPriority = "interactive" | "normal" | "speculative";

export const HUB_GET_ADMISSION_STORAGE_KEY = "hub-get-admission-limit";
const DEFAULT_GET_ADMISSION_LIMIT = 6;
const PRIORITY_RANK: Record<ApiFetchPriority, number> = {
  interactive: 0,
  normal: 1,
  speculative: 2,
};

type Waiter = {
  id: number;
  priority: ApiFetchPriority;
  enqueuedAtMs: number;
  resolve: (permit: ApiFetchPermit) => void;
  reject: (error: Error) => void;
  signal?: AbortSignal;
  onAbort?: () => void;
};

export type ApiFetchPermit = {
  grantedAtMs: number;
  release: () => void;
};

export type ApiFetchAdmissionSnapshot = {
  limit: number | null;
  active: number;
  queued: number;
  queuedByPriority: Record<ApiFetchPriority, number>;
};

let active = 0;
let nextWaiterId = 1;
const waiters: Waiter[] = [];

function configuredLimit(): number {
  let raw: string | null | undefined;
  try {
    raw = globalThis.localStorage?.getItem(HUB_GET_ADMISSION_STORAGE_KEY);
  } catch {
    raw = null;
  }
  if (!raw) {
    raw = (import.meta.env as Record<string, string | undefined> | undefined)
      ?.VITE_HUB_GET_ADMISSION_LIMIT;
  }
  if (raw?.trim().toLowerCase() === "unlimited") {
    return Number.POSITIVE_INFINITY;
  }
  const parsed = raw ? Number.parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_GET_ADMISSION_LIMIT;
}

function abortError(): DOMException {
  return new DOMException("The operation was aborted.", "AbortError");
}

function removeWaiter(waiter: Waiter): void {
  const index = waiters.indexOf(waiter);
  if (index >= 0) {
    waiters.splice(index, 1);
  }
  if (waiter.signal && waiter.onAbort) {
    waiter.signal.removeEventListener("abort", waiter.onAbort);
  }
}

function grant(waiter: Waiter): void {
  removeWaiter(waiter);
  active += 1;
  let released = false;
  waiter.resolve({
    grantedAtMs: Date.now(),
    release: () => {
      if (released) return;
      released = true;
      active = Math.max(0, active - 1);
      drain();
    },
  });
}

function drain(): void {
  const limit = configuredLimit();
  while (active < limit && waiters.length > 0) {
    waiters.sort(
      (left, right) =>
        PRIORITY_RANK[left.priority] - PRIORITY_RANK[right.priority] ||
        left.id - right.id,
    );
    const waiter = waiters[0];
    if (!waiter) return;
    if (waiter.signal?.aborted) {
      removeWaiter(waiter);
      waiter.reject(abortError());
      continue;
    }
    grant(waiter);
  }
}

export async function acquireApiFetchPermit(
  priority: ApiFetchPriority,
  signal?: AbortSignal,
): Promise<ApiFetchPermit> {
  if (signal?.aborted) {
    throw abortError();
  }
  const limit = configuredLimit();
  if (active < limit) {
    active += 1;
    let released = false;
    return {
      grantedAtMs: Date.now(),
      release: () => {
        if (released) return;
        released = true;
        active = Math.max(0, active - 1);
        drain();
      },
    };
  }
  return new Promise<ApiFetchPermit>((resolve, reject) => {
    const waiter: Waiter = {
      id: nextWaiterId++,
      priority,
      enqueuedAtMs: Date.now(),
      resolve,
      reject,
      signal,
    };
    waiter.onAbort = () => {
      removeWaiter(waiter);
      reject(abortError());
    };
    signal?.addEventListener("abort", waiter.onAbort, { once: true });
    waiters.push(waiter);
    drain();
  });
}

export function getApiFetchAdmissionSnapshot(): ApiFetchAdmissionSnapshot {
  const limit = configuredLimit();
  return {
    limit: Number.isFinite(limit) ? limit : null,
    active,
    queued: waiters.length,
    queuedByPriority: {
      interactive: waiters.filter((entry) => entry.priority === "interactive").length,
      normal: waiters.filter((entry) => entry.priority === "normal").length,
      speculative: waiters.filter((entry) => entry.priority === "speculative").length,
    },
  };
}

export function resetApiFetchAdmissionForTests(): void {
  for (const waiter of [...waiters]) {
    removeWaiter(waiter);
    waiter.reject(abortError());
  }
  active = 0;
  nextWaiterId = 1;
}
